import { AlertTriangle } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { toApiError } from '@/api/errors'
import { useStores } from '@/stores'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { usePresence } from '@/hooks/usePresence'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import { InlineAlert } from './InlineAlert'

type DialogProps = {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
}

const sizes = { sm: 'max-w-[420px]', md: 'max-w-[520px]', lg: 'max-w-[680px]', xl: 'max-w-[1040px]', '2xl': 'max-w-[1480px] max-h-[calc(100vh-2rem)] overflow-y-auto' }

export function Dialog({ open, onClose, title, description, children, footer, size = 'sm' }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null)
  const presence = usePresence(open, 160)
  const outsideClose = useStores().appearance.settings.outsideClose
  useFocusTrap(ref, open, onClose)
  if (!presence.mounted) return null

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        data-state={presence.state}
        className="motion-overlay absolute inset-0 bg-[rgba(8,14,16,0.28)] backdrop-blur-[3px] dark:bg-[rgba(0,0,0,0.55)]"
        onClick={outsideClose ? onClose : undefined}
        onMouseDown={(event) => event.preventDefault()}
        aria-hidden
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        tabIndex={-1}
        data-state={presence.state}
        className={cn('glass-strong motion-dialog relative w-full rounded-[var(--radius-panel)] p-6 outline-none', sizes[size])}
      >
        <h2 id="dialog-title" className="text-[length:calc(16px*var(--text-scale))] font-semibold tracking-[-0.01em] text-fg">
          {title}
        </h2>
        {description && <div className="mt-1.5 text-[length:calc(13.5px*var(--text-scale))] leading-relaxed text-muted">{description}</div>}
        {children && <div className="mt-4">{children}</div>}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

type ConfirmProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<unknown> | unknown
  title: string
  description?: ReactNode
  confirmLabel?: string
  tone?: 'danger' | 'primary'
}

/** Destructive confirmation. A backend CONFLICT stays visible inside the dialog. */
export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel, tone = 'danger' }: ConfirmProps) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    if (busy) return
    setError(null)
    onClose()
  }

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
      setBusy(false)
      onClose()
    } catch (err) {
      const apiError = toApiError(err)
      setError(apiError.isConflict ? t('errors.cannotComplete', { message: apiError.message }) : apiError.message)
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title={
        <span className="flex items-center gap-2">
          {tone === 'danger' && <AlertTriangle className="size-4.5 text-danger" aria-hidden />}
          {title}
        </span>
      }
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={confirm} loading={busy} data-autofocus>
            {confirmLabel ?? t('common.actions.confirm')}
          </Button>
        </>
      }
    >
      {error && <InlineAlert tone="danger">{error}</InlineAlert>}
    </Dialog>
  )
}
